import config from '@config';
import { loadCssFile, loadJavaScriptFile } from '@xFrame4/common/Functions';

export class LazyLoadingHelper
{
    loadingCssFontsStarted = false;
    loadingJavascriptFilesStarted = false;

    setup()
    {
        this.lazyLoadImages();
        this.lazyLoadIframes();
        this.lazyLoadJavascriptFiles();
        this.lazyLoadCssFonts();
        this.lazyLoadEmbedSocialReviews();
        this.lazyLoadHCaptcha();
    }

    /**
     * Lazy load the images.
     * 
     * @param verticalLazyLoadThreshold The threshold in pixels from where to start loading the images.
     * @param horizontalLazyLoadThreshold The threshold in pixels from where to start loading the images.
     */
    lazyLoadImages(verticalLazyLoadThreshold: number = 200, horizontalLazyLoadThreshold: number = 50): void
    {
        let options: IntersectionObserverInit = {
            root: null,
            rootMargin: `${horizontalLazyLoadThreshold}px ${verticalLazyLoadThreshold}px`, // horizontal and vertical margins
            threshold: 0,
        };

        const loadImage = (image: HTMLImageElement): void =>
        {
            const src = image.getAttribute('data-src');
            if (src)
            {
                image.src = src;
                image.removeAttribute('data-src');
            }
        };

        const onIntersection: IntersectionObserverCallback = (entries, observer) =>
        {
            entries.forEach((entry) =>
            {
                if (entry.isIntersecting)
                {
                    const image = entry.target as HTMLImageElement;
                    loadImage(image);
                    observer.unobserve(image);
                }
            });
        };

        const observeImages = (images: NodeListOf<Element>, observer: IntersectionObserver): void =>
        {
            images.forEach((image) =>
            {
                observer.observe(image);
            });
        };

        let observer = new IntersectionObserver(onIntersection, options);
        let images = document.querySelectorAll('img[smooth-image]');

        observeImages(images, observer);

        // Create a MutationObserver to watch for new images added to the DOM.
        const mutationObserver = new MutationObserver((mutations) =>
        {
            mutations.forEach((mutation) =>
            {
                if (mutation.type === 'childList' && mutation.addedNodes.length > 0)
                {
                    mutation.addedNodes.forEach((node) =>
                    {
                        if (node.nodeType === Node.ELEMENT_NODE)
                        {
                            let element = node as HTMLElement;
                            let newImages = element.querySelectorAll('img[smooth-image]');

                            if (newImages.length > 0)
                            {
                                observeImages(newImages, observer);
                            }
                        }
                    });
                }
            });
        });

        let observerConfig: MutationObserverInit = {
            childList: true,
            subtree: true,
        };

        mutationObserver.observe(document.body, observerConfig);
    }

    /**
     * Lazy load iframes.
     * 
     * @param lazyLoadThreshold The threshold in pixels.
     */
    lazyLoadIframes(lazyLoadThreshold: number = 200): void
    {
        let options: IntersectionObserverInit = {
            root: null,
            rootMargin: `${lazyLoadThreshold}px`,
            threshold: 0,
        };

        const loadIframe = (iframe: HTMLIFrameElement): void =>
        {
            const src = iframe.getAttribute('data-src');
            if (src)
            {
                iframe.src = src;
                iframe.removeAttribute('data-src');
            }
        };

        const onIntersection: IntersectionObserverCallback = (entries, observer) =>
        {
            entries.forEach((entry) =>
            {
                if (entry.isIntersecting)
                {
                    const iframe = entry.target as HTMLIFrameElement;
                    loadIframe(iframe);
                    observer.unobserve(iframe);
                }
            });
        };

        const observeIframes = (iframes: NodeListOf<Element>, observer: IntersectionObserver): void =>
        {
            iframes.forEach((iframe) =>
            {
                observer.observe(iframe);
            });
        };

        let observer = new IntersectionObserver(onIntersection, options);
        let iframes = document.querySelectorAll('iframe[smooth-iframe]');

        observeIframes(iframes, observer);

        // Create a MutationObserver to watch for new iframes added to the DOM.
        const mutationObserver = new MutationObserver((mutations) =>
        {
            mutations.forEach((mutation) =>
            {
                if (mutation.type === 'childList' && mutation.addedNodes.length > 0)
                {
                    mutation.addedNodes.forEach((node) =>
                    {
                        if (node.nodeType === Node.ELEMENT_NODE)
                        {
                            const element = node as HTMLElement;
                            const newIframes = element.querySelectorAll('iframe[smooth-iframe]');

                            if (newIframes.length > 0)
                            {
                                observeIframes(newIframes, observer);
                            }
                        }
                    });
                }
            });
        });

        const observerConfig: MutationObserverInit = {
            childList: true,
            subtree: true,
        };

        mutationObserver.observe(document.body, observerConfig);
    }

    /**
     * Lazy load the javascript files when a user interacts with the page or after a certain amount of time expires after the page load.
     */
    lazyLoadJavascriptFiles()
    {
        // user interaction
        addEventListener('scroll', () => this.loadJavascriptFiles());
        addEventListener('mousemove', () => this.loadJavascriptFiles());
        addEventListener('touchstart', () => this.loadJavascriptFiles());
        addEventListener('touchmove', () => this.loadJavascriptFiles());
        addEventListener('click', () => this.loadJavascriptFiles());

        // timeout
        let timeout = 5000;
        setTimeout(() => this.loadJavascriptFiles(), timeout);
    }

    /**
     * Load the javascript files.
     */
    async loadJavascriptFiles()
    {
        if (this.loadingJavascriptFilesStarted) return;
        this.loadingJavascriptFilesStarted = true;

        let jsFiles = [
            //{ type: 'local', url: 'split/Migrate.js' },
            //{ type: 'remote', url: 'https://cdn.jsdelivr.net/npm/popper.js@1.14.3/dist/umd/popper.min.js' },
            //{ type: 'remote', url: 'https://cdn.jsdelivr.net/npm/bootstrap@4.1.3/dist/js/bootstrap.min.js' },
            { type: 'local', url: 'split/Abstract.js' },
            { type: 'local', url: 'split/Cookie.js' },
            { type: 'local', url: 'split/Countdown.js' },
            { type: 'local', url: 'split/CustomEvent.js' },
            //{ type: 'local', url: 'split/Device.js.js' },
            { type: 'local', url: 'split/Easing.js' },
            { type: 'local', url: 'split/Form.js' },
            { type: 'local', url: 'split/Isotope.js' },
            //{ type: 'local', url: 'split/Lightgallery.js' },
            { type: 'local', url: 'split/Materianize.js' },
            //{ type: 'local', url: 'split/MultiToggles.js' },
            //{ type: 'local', url: 'split/Owl.js' },
            { type: 'local', url: 'split/PageTransition.js' },
            { type: 'local', url: 'split/RDCalendar.js' },
            { type: 'local', url: 'split/RDInputLabel.js' },
            //{ type: 'local', url: 'split/RDNavbar.js' },
            { type: 'local', url: 'split/Regula.js' },
            { type: 'local', url: 'split/ResizeEvent.js' },
            { type: 'local', url: 'split/ScrollHandler.js' },
            //{ type: 'local', url: 'split/Select2.js' },
            { type: 'local', url: 'split/SimpleFilter.js' },
            { type: 'local', url: 'split/Slick.js' },
            { type: 'local', url: 'split/Stepper.js' },
            { type: 'local', url: 'split/TouchSwipe.js' },
            //{ type: 'local', url: 'split/UIToTop.js' },
            { type: 'local', url: 'split/Vide.js' },
            //{ type: 'local', url: 'split/WOW.js' },
            { type: 'local', url: 'split/αCountdown.js' },
            //{ type: 'local', url: 'split/αCounter.js' },
            { type: 'local', url: 'split/αProgressCircle.js' },
            //{ type: 'local', url: 'split/αUtil.js' },
            //{ type: 'local', url: 'jquery-listnav.min.js' },
            //{ type: 'local', url: 'jquery.sticky.js' },
            //{ type: 'local', url: 'script_v_2.js' },
            //{ type: 'local', url: 'my_v7.js' },
        ];

        for (let jsFile of jsFiles)
        {
            if (jsFile.type === 'local')
            {
                await loadJavaScriptFile(config.defaultPublicDirBaseUrl + 'template/js/' + jsFile.url);
            }
            else if (jsFile.type === 'remote')
            {
                await loadJavaScriptFile(jsFile.url);
            }
        }
    }

    /**
     * Lazy load the CSS fonts when a user interects with the page or after a certain amount of time expires after the page load.
     */
    lazyLoadCssFonts()
    {
        // user interaction
        addEventListener('scroll', () => this.loadCssFonts());
        addEventListener('mousemove', () => this.loadCssFonts());
        addEventListener('touchstart', () => this.loadCssFonts());
        addEventListener('touchmove', () => this.loadCssFonts());
        addEventListener('click', () => this.loadCssFonts());

        // timeout
        let timeout = 5000;
        setTimeout(() => this.loadCssFonts(), timeout);
    }

    /**
     * Load the CSS fonts
     */
    loadCssFonts()
    {
        if (this.loadingCssFontsStarted) return;
        this.loadingCssFontsStarted = true;

        let cssFiles = [
            { url: 'https://cdnjs.cloudflare.com/ajax/libs/font-awesome/4.7.0/css/font-awesome.css' },
            { url: 'https://cdnjs.cloudflare.com/ajax/libs/MaterialDesign-Webfont/1.4.57/css/materialdesignicons.css' },
            { url: config.defaultPublicDirBaseUrl + 'template/css/linearicons.css' }
        ];

        cssFiles.forEach((file) =>
        {
            loadCssFile(file.url);
        });
    }

    /**
     * Lazy load the EmbedSocialReviews script
     */
    lazyLoadEmbedSocialReviews(): void
    {
        // Load the EmbedSocialReviews script if not already loaded
        const loadScript = () =>
        {
            const existingScript = document.getElementById("EmbedSocialReviewsScript");

            if (existingScript)
            {
                return;
            }

            const script = document.createElement("script");
            script.id = "EmbedSocialReviewsScript";
            script.src = "https://embedsocial.com/embedscript/ri.js";
            document.head.appendChild(script);
        };

        // Initialize Intersection Observer
        const initObserver = () =>
        {
            const options: IntersectionObserverInit = {
                root: null,
                rootMargin: '200px',
                threshold: 0
            };

            const observer = new IntersectionObserver((entries, observer) =>
            {
                entries.forEach(entry =>
                {
                    if (entry.isIntersecting)
                    {
                        loadScript();
                        observer.unobserve(entry.target);
                    }
                });
            }, options);

            const embedSocialReviews = document.querySelector('.embedsocial-reviews');
            if (embedSocialReviews)
            {
                observer.observe(embedSocialReviews);
            }
        };

        // Run the initObserver function when the DOM is fully loaded
        if (document.readyState === 'loading')
        {
            document.addEventListener("DOMContentLoaded", initObserver);
        } else
        {
            initObserver();
        }
    }

    /**
     * Lazy load the hCaptcha script.
     * 
     * @param lazyLoadThreshold 
     */
    lazyLoadHCaptcha(lazyLoadThreshold: number = 200): void
    {
        let options: IntersectionObserverInit = {
            root: null,
            rootMargin: `${lazyLoadThreshold}px`,
            threshold: 0,
        };

        const loadHCaptchaScript = (): void =>
        {
            const script = document.createElement('script');
            script.src = 'https://www.hCaptcha.com/1/api.js?hl=ro';
            script.async = true;
            script.defer = true;
            document.head.appendChild(script);
        };

        const onIntersection: IntersectionObserverCallback = (entries, observer) =>
        {
            entries.forEach((entry) =>
            {
                if (entry.isIntersecting)
                {
                    const captchaContainer = entry.target as HTMLElement;
                    loadHCaptchaScript();
                    observer.unobserve(captchaContainer);
                }
            });
        };

        const observeCaptchaContainers = (containers: NodeListOf<Element>, observer: IntersectionObserver): void =>
        {
            containers.forEach((container) =>
            {
                observer.observe(container);
            });
        };

        let observer = new IntersectionObserver(onIntersection, options);
        let captchaContainers = document.querySelectorAll('.h-captcha[lazy-captcha]');

        observeCaptchaContainers(captchaContainers, observer);
    }
}

export default LazyLoadingHelper;